import { Injectable } from '@angular/core';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { InvoiceAction } from '../../domain/enums/invoice-action.enum';
import { InvoiceRepository } from '../../domain/interfaces/invoice-repository.interface';
import { InvoiceExportRequest } from '../../domain/models/invoice-export.model';
import { InvoiceList } from '../../domain/models/invoice-list.model';
import { InvoiceRevenueEditData, InvoiceRevenueEditRequest } from '../../domain/models/invoice-revenue-edit.model';
import { InvoiceMapper } from '../mappers/invoice.mapper';
import { InvoiceRevenueEditMapper } from '../mappers/invoice-revenue-edit.mapper';
import { InvoiceApiService } from '../services/invoice-api.service';

@Injectable()
export class HttpInvoiceRepository implements InvoiceRepository {
  constructor(
    private readonly api: InvoiceApiService,
    private readonly mapper: InvoiceMapper,
    private readonly revenueEditMapper: InvoiceRevenueEditMapper
  ) {}

  getInvoices(query: string): Observable<InvoiceList> {
    return this.api.getInvoices(query).pipe(
      map((response) => ({
        items: response['hydra:member'].map((item) => this.mapper.fromDto(item)),
        total: response['hydra:totalItems']
      }))
    );
  }

  getIssuedCount(): Observable<number> {
    return this.api.getIssuedCount();
  }

  getErrorCount(): Observable<number> {
    return this.api.getErrorCount();
  }

  executeAction(invoiceId: number | string, action: InvoiceAction, serviceInvoiceId: (number | string)): Observable<void | Blob> {
    if (action === InvoiceAction.Cancel) {
      return this.api.cancelInvoice(invoiceId);
    }

    if (action === InvoiceAction.Resend || action === InvoiceAction.RefreshStatus) {
      return this.api.resendInvoice(invoiceId);
    }

    if (action === InvoiceAction.DownloadDanfe) {
      return this.api.downloadDanfe(serviceInvoiceId);
    }

    if (action === InvoiceAction.SendEmail) {
      return this.api.sendEmail(serviceInvoiceId);
    }

    return of(undefined);
  }

  exportCsv(request: InvoiceExportRequest): Observable<Blob> {
    return this.api.exportInvoicesCsv(request);
  }

  getRevenueEditData(revenueId: number): Observable<InvoiceRevenueEditData> {
    return this.api.getRevenueInvoiceEvent(revenueId).pipe(
      map((response) => this.revenueEditMapper.fromEventDto(response))
    );
  }

  updateRevenue(request: InvoiceRevenueEditRequest): Observable<InvoiceRevenueEditData> {
    const dto = this.revenueEditMapper.toUpdateDto(request);

    return this.api.updateRevenueInvoiceEvent(dto).pipe(
      switchMap(() => this.getRevenueEditData(request.revenueId))
    );
  }
}
